import { useRef, useState, type DragEvent } from 'react'
import { DEAL_STAGES, DEAL_STAGE_COLORS } from '../../lib/stages'
import type { PipelineDeal } from './usePipeline'
import { closeHint, columnTotal, dealCompany, formatMoney, matchesSearch } from './pipelineMath'

/**
 * The Deals kanban. Mirrors legacy `renderKanban()` + `dealCard()` in
 * crm/index.html: one column per deal stage, a count and value total in
 * each header, cards that open the (still legacy) deal drawer, a
 * "+ Add Deal" button per column that opens the legacy add modal with the
 * stage preset, and HTML5 drag-and-drop between columns.
 *
 * Uses the host's `.kanban*`, `.k-*` and `.deal-card*` classes so the board
 * looks the same as before.
 */

interface Props {
  deals: PipelineDeal[]
  search: string
  onMove: (id: string, stage: string) => void
  onOpenDeal: (id: string) => void
  onAddDeal: (stage: string) => void
}

export function PipelineBoard({ deals, search, onMove, onOpenDeal, onAddDeal }: Props) {
  const dragId = useRef<string | null>(null)
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [overStage, setOverStage] = useState<string | null>(null)

  const onDragStart = (e: DragEvent<HTMLDivElement>, id: string) => {
    dragId.current = id
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move'
      // Firefox will not start a drag without data.
      e.dataTransfer.setData('text/plain', id)
    }
    setDraggingId(id)
  }
  const onDragEnd = () => {
    setDraggingId(null)
    setOverStage(null)
  }
  const onDrop = (e: DragEvent<HTMLDivElement>, stage: string) => {
    e.preventDefault()
    setOverStage(null)
    const id = dragId.current
    dragId.current = null
    setDraggingId(null)
    if (id) onMove(id, stage)
  }

  return (
    <div className="kanban-wrap">
      <div className="kanban" role="list" aria-label="Deals by stage">
        {DEAL_STAGES.map((stage) => {
          const cards = deals.filter((d) => d.stage === stage && matchesSearch(d, search))
          const total = columnTotal(cards)
          return (
            <section className="k-col" key={stage} role="listitem" aria-label={stage}>
              <div className="k-col-head">
                <div className="k-col-title">
                  <span
                    className="k-col-dot"
                    aria-hidden="true"
                    style={{ background: DEAL_STAGE_COLORS[stage] }}
                  />
                  {stage}
                </div>
                <div className="k-col-meta">
                  <span className="k-count" aria-label={`${cards.length} deals`}>
                    {cards.length}
                  </span>
                  {total > 0 && (
                    <span className="k-value" data-testid={`total-${stage}`}>
                      {formatMoney(total)}
                    </span>
                  )}
                </div>
              </div>
              <div
                className={overStage === stage ? 'k-body drag-over' : 'k-body'}
                data-stage={stage}
                data-testid={`column-${stage}`}
                onDragOver={(e) => {
                  e.preventDefault()
                  if (overStage !== stage) setOverStage(stage)
                }}
                onDragLeave={() => setOverStage((s) => (s === stage ? null : s))}
                onDrop={(e) => onDrop(e, stage)}
              >
                {cards.map((d) => (
                  <DealCard
                    key={d.id}
                    deal={d}
                    dragging={draggingId === d.id}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    onOpen={onOpenDeal}
                  />
                ))}
                <button type="button" className="k-add-btn" onClick={() => onAddDeal(stage)}>
                  + Add deal
                </button>
              </div>
            </section>
          )
        })}
      </div>
    </div>
  )
}

function DealCard({
  deal: d,
  dragging,
  onDragStart,
  onDragEnd,
  onOpen,
}: {
  deal: PipelineDeal
  dragging: boolean
  onDragStart: (e: DragEvent<HTMLDivElement>, id: string) => void
  onDragEnd: () => void
  onOpen: (id: string) => void
}) {
  const co = dealCompany(d)
  const value = Number(d.value) || 0
  const hint = d.close_date ? closeHint(d.close_date, d.stage) : null
  return (
    <div
      className={dragging ? 'deal-card dragging' : 'deal-card'}
      draggable
      role="button"
      tabIndex={0}
      data-testid={`deal-${d.id}`}
      onDragStart={(e) => onDragStart(e, d.id)}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(d.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen(d.id)
        }
      }}
    >
      <div className="deal-card-title">{d.title}</div>
      {co && (
        <div className="deal-card-co">
          <span className="deal-card-co-name">{co}</span>
        </div>
      )}
      <div className="deal-card-footer">
        <span className="deal-value">{value ? formatMoney(value) : ''}</span>
        <span className={hint?.overdue ? 'deal-date overdue' : 'deal-date'}>
          {hint?.text ?? ''}
        </span>
      </div>
    </div>
  )
}
